import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";
import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { ga } from "client/utils/adsense/ga";
import { gtag } from "client/utils/adsense/gtag";
import { downloadFromLink } from "client/utils/downloadFromLink";
import { getImageUrl } from "client/utils/getImageUrl";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { sleep } from "client/utils/sleep";
import { useContext, useRef, useState } from "react";
import { store } from "store/store";

interface WorkerResponse {
    url: string;
    title: string;
    messageId: string;
}

export const useImageCropperAction = () => {
    const { initialImageRef: initialCanvasRef, imageLoaded } = useContext(CanvasContext);
    const [isProcessing, setIsProcessing] = useState(false);
    const isActive = imageLoaded && !isProcessing;
    const { consumeQuotaByFrontendTool } = useDailyQuota();
    const isQuotaConsumedRef = useRef<boolean>(false);

    const handleClickAnalytics = () => {
        ga("send", {
            hitType: "pageview",
            page: "/vp/image_cropper/export_started/",
            title: "Image Cropper Export Started",
        });

        // event
        gtag("event", "Export Started", {
            event_category: "Image Cropper",
            event_label: "Editor Page",
        });
    };

    const handleClick = async () => {
        const image = store.getState().crop.image;
        if (!image) return;

        handleClickAnalytics();
        setIsProcessing(true);

        try {
            const crop = store.getState().crop.crop;
            // Create worker
            const worker = new Worker(new URL("./imageCropperWebWorker.ts", import.meta.url));

            const messageId = crypto.randomUUID();
            // Create a promise to handle worker response
            const processImage = new Promise<WorkerResponse>((resolve, reject) => {
                worker.onmessage = (e) => {
                    if (e.data.messageId !== messageId) return; // Ignore messages not intended for this promise
                    if (e.data.error) {
                        reject(new Error(e.data.error));
                    } else {
                        resolve(e.data);
                    }
                };

                worker.onerror = (error) => {
                    reject(error);
                };
            });

            // Get image URL
            const imageUrl = initialCanvasRef?.current?.getAttribute("src") ?? getImageUrl(image.src);

            // Send data to worker
            worker.postMessage({
                imageUrl,
                crop,
                title: image.title,
                messageId,
            });

            // Wait for worker to complete
            const { url, title } = await processImage;

            if (!isQuotaConsumedRef.current) {
                consumeQuotaByFrontendTool();
                isQuotaConsumedRef.current = true;
            }

            // Download the processed image
            downloadFromLink(url, title);

            // Cleanup
            worker.terminate();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error processing image:", error);
            // Fallback to regular processing if worker fails
            await handleClickRegular();
        } finally {
            setIsProcessing(false);
        }
    };

    // Fallback function for regular processing
    const handleClickRegular = async () => {
        const image = store.getState().crop.image;
        if (!image) return;
        const crop = store.getState().crop.crop;
        handleClickAnalytics();
        setIsProcessing(true);
        await sleep(100);

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) return;

        canvas.width = crop.width;
        canvas.height = crop.height;

        const { image: _img } = await loadImage(
            initialCanvasRef?.current?.getAttribute("src") ?? getImageUrl(image.src),
        );

        context.drawImage(_img, crop.left, crop.top, crop.width, crop.height, 0, 0, crop.width, crop.height);
        const url = canvas.toDataURL("image/jpeg", image.title);
        if (!isQuotaConsumedRef.current) {
            consumeQuotaByFrontendTool();
            isQuotaConsumedRef.current = true;
        }
        downloadFromLink(url, image.title);
        setIsProcessing(false);
    };

    return {
        handleClick,
        isActive,
        imageLoaded,
        isProcessing,
    };
};

